/** The takeoff toolbar for the web: the shared descriptors (shared-ui/takeoff/toolbarModel,
 *  which the native app reads too) plus the DOM-only pieces, the icon renderer and the
 *  click-anchor helper. Re-exported whole so existing callers keep one import path. */

import React from 'react';
import { TOOL_GLYPHS, INK, type ToolGlyphName } from '../../../shared-ui/takeoff/toolGlyphs';
import type { AdskGlyphName } from '../../../shared-ui/takeoff/adskGlyphName';
import type { ToolbarContext } from '../../../shared-ui/takeoff/toolbarModel';
import { AdskGlyph } from '../../../platform/apsViewer/AdskGlyph';

export * from '../../../shared-ui/takeoff/toolbarModel';

/** A registry glyph as a DOM svg, sized and coloured by className like the old icon components. */
export function GlyphIcon({ name, className }: { name: ToolGlyphName; className?: string }) {
  const glyph = TOOL_GLYPHS[name];
  return (
    <svg viewBox={glyph.viewBox} className={className} aria-hidden="true">
      {glyph.layers.map((layer, index) => (
        <path
          key={index}
          d={layer.d}
          fill={layer.fill === INK ? 'currentColor' : layer.fill}
          fillOpacity={layer.fillOpacity}
          stroke={layer.stroke === INK ? 'currentColor' : layer.stroke}
          strokeWidth={layer.strokeWidth}
          strokeLinecap={layer.strokeLinecap}
          strokeLinejoin={layer.strokeLinejoin}
        />
      ))}
    </svg>
  );
}

// ── One icon chooser for every renderer ─────────────────────────────────────

// One component per glyph, so AdskGlyph's fallback keeps a stable type across renders.
const glyphComponents = new Map<ToolGlyphName, React.ComponentType<{ className?: string }>>();
function glyphComponent(name: ToolGlyphName) {
  let component = glyphComponents.get(name);
  if (!component) {
    component = ({ className }) => <GlyphIcon name={name} className={className} />;
    glyphComponents.set(name, component);
  }
  return component;
}

/** The icon a registry entry wears in the current context: Autodesk's glyph on
 *  the Autodesk surface when the tool has one, the shared icon otherwise. The
 *  rail and the mobile toolbar both go through this so they cannot drift. */
export function ToolIcon({
  entry,
  ctx,
  className,
}: {
  entry: { glyph: ToolGlyphName; apsIcon?: AdskGlyphName };
  ctx: ToolbarContext;
  className?: string;
}) {
  if (ctx.isApsViewer && entry.apsIcon) {
    return <AdskGlyph name={entry.apsIcon} fallback={glyphComponent(entry.glyph)} className={className} />;
  }
  return <GlyphIcon name={entry.glyph} className={className} />;
}

/** A control's rect as plain numbers, so a window event can carry where the
 *  click came from and the viewer can open its panel beside it. */
export interface AnchorRect { x: number; y: number; width: number; height: number }

export function anchorRect(el: Element | null | undefined): AnchorRect | null {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { x: r.x, y: r.y, width: r.width, height: r.height };
}
